/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { generatePath, matchPath } from "react-router-dom";
import { IRouteListEntry, IRouteResolver } from "@adapptio/lib-blueprint";
import {
	IResolvedLink,
	IResolvedLocation,
	IResolveLinkOpts,
	isLinkExternal,
	TLinkLocationSpec
} from "@adapptio/lib-components";
import { BeforeNavigateHookHandler, INavigationResolver, IRoutingManager } from "@adapptio/lib-core";
import { createEventEmitter, createHook, emitEvent, isNumber } from "@adapptio/lib-shared";
import { stringify as stringifyQs, parse as parseQs } from "qs";

import { ResourceService } from "../resources/ResourceService";

export class EditorRoutingManager implements IRoutingManager {
	/** Route resolver */
	public readonly routeResolver: IRouteResolver;

	/** Navigation resolver */
	public readonly navigationResolver: INavigationResolver;

	/**
	 * Navigate event - emitted when location change
	 */
	public readonly onNavigate = createEventEmitter<void>();

	/**
	 * Hook that is called before navigation.
	 */
	public beforeNavigateHook = createHook<BeforeNavigateHookHandler>();

	/** Prevent navigation flag */
	public preventNavigate = false;

	/* Current virtualized location */
	private readonly history: IResolvedLocation[] = [];
	private historyIndex = 0;

	/**
	 * Constructor
	 */
	public constructor(private resourceSvc: ResourceService, defaultPath?: string) {
		this.routeResolver = {
			getRouteByName: (routeName) => {
				return this.resourceSvc.getRouteByName(routeName);
			},
			getRouteList: () => {
				return this.resourceSvc.getRouteList();
			},
			onInvalidate: createEventEmitter()
		};

		this.navigationResolver = {
			getCurrentLocation: () => this.getCurrentLocation(),
			resolveLink: (linkSpec: TLinkLocationSpec, opts: IResolveLinkOpts) =>
				this.resolveLink(linkSpec, opts),
			onNavigate: this.onNavigate,
			beforeNavigateHook: this.beforeNavigateHook
		};

		this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
		window.addEventListener("beforeunload", this.handleBeforeUnload);

		if (defaultPath) {
			this.addPathToHistory(defaultPath);
		} else {
			this.addPathToHistory("");
		}
	}

	private addPathToHistory(path: string, preventSetCurrent = false): void {
		const location = this.parseLocationFromString(path);

		this.history.length = this.historyIndex + 1;

		this.history.push(location);

		if (!preventSetCurrent) {
			this.historyIndex = this.history.length - 1;
		}
	}

	private handleBeforeUnload(ev: BeforeUnloadEvent) {
		if (this.preventNavigate) {
			ev.preventDefault();
			ev.returnValue = "";
		}
	}

	private async handleBeforeNavigateHook(path: string|number): Promise<boolean> {
		try {
			const result = await Promise.all(this.beforeNavigateHook.handlers.map((handler) => handler(path)));
			const hasFalseResult = result.some((r) => r === false);

			if (this.preventNavigate) {
				return false;
			}

			return hasFalseResult ? false : true;
		} catch (err) {
			console.error("[RoutingManager] Error in beforeNavigate hook", err);
			return true;
		}
	}

	/**
	 * Return current location
	 */
	public getCurrentLocation(): IResolvedLocation {
		return this.history[this.historyIndex];
	}

	/**
	 * Returns route object by name
	 *
	 * @param routeName Route name
	 */
	private getRouteByName(routeName: string): IRouteListEntry {
		return this.resourceSvc.getRouteByName(routeName);
	}

	/**
	 * Navigates to a new path (without reload)
	 *
	 * @param path New path
	 */
	public navigate(path: string | number): void {
		this.handleBeforeNavigateHook(path).then((canNavigate) => {
			if (!canNavigate) {
				console.debug("[RoutingManager] Navigation prevented by hook");
				return;
			}

			if (isNumber(path)) {
				this.historyIndex = this.historyIndex + path;
			} else {
				this.addPathToHistory(path);
			}

			emitEvent(this.onNavigate);
		});
	}

	/**
	 * Sets prevent navigation flag
	 * If set to true, navigation will be blocked incl. browser back/forward/close tab
	 *
	 * @param prevent Prevent navigation flag
	 */
	public setPreventNavigation(prevent: boolean) {
		this.preventNavigate = prevent;
	}

	/**
	 * Resolves link based on link schema spec and options
	 *
	 * @param linkSpec Location specification
	 * @param opts Options
	 */
	public resolveLink(linkSpec: TLinkLocationSpec, opts: IResolveLinkOpts): IResolvedLink {
		const location = this.getCurrentLocation();

		let linkUrl: string;
		let linkPath: string = null;

		switch (linkSpec.type) {
			case "ROUTE": {
				const route = this.getRouteByName(linkSpec.value.ROUTE.name);

				if (!route) {
					linkUrl = "#route-not-found";
					break;
				}

				try {
					linkUrl = linkPath = generatePath(route.path, linkSpec.value.ROUTE.params);
					const qs = stringifyQs(linkSpec.value.ROUTE.queryParams, {
						skipNulls: !linkSpec.value.ROUTE.serializeNullParams
					});

					if (qs) {
						linkUrl = linkUrl + "?" + qs;
					}
				} catch (err) {
					linkUrl = "#route-has-invalid-params";
					break;
				}

				break;
			}

			case "VIEW": {
				const route = this.getRouteByName("view");

				if (!route) {
					linkUrl = "#view-route-not-configured";
					break;
				}

				try {
					linkPath = generatePath(route.path, {
						viewId: linkSpec.value.VIEW.viewId
					});

					linkUrl =
						linkPath +
						"?" +
						stringifyQs({
							viewParams: linkSpec.value.VIEW.params
						});
				} catch (err) {
					linkUrl = "#view-route-has-invalid-path-configured";
					break;
				}

				break;
			}

			case "URL": {
				linkUrl = linkPath = linkSpec.value.URL;

				break;
			}

			case "NONE": {
				linkUrl = "";

				break;
			}
		}

		return {
			url: linkUrl,
			isExternal: isLinkExternal(linkUrl),
			match: linkPath
				? matchPath(location.pathname, {
					path: linkPath,
					exact: opts.exact,
					strict: opts.strict
				})
				: null
		};
	}

	/**
	 * Returns current location as full string path
	 * Note: keeps the original `path` value when `setFromString`.
	 */
	public getCurrentLocationAsString(): string {
		return this.locationToString(this.getCurrentLocation());
	}

	private parseLocationFromString(path: string): IResolvedLocation {
		const [ basePath, hash ] = path.split("#", 2);
		const [ pathName, queryStr ] = basePath.split("?");

		return {
			pathname: pathName,
			query: parseQs(queryStr),
			hash: hash
		};
	}

	private locationToString({ pathname, query, hash }: IResolvedLocation): string {
		let path = pathname;
		const queryString = query && stringifyQs(query);

		if (queryString) {
			path += `?${queryString}`;
		}

		if (hash) {
			path += `#${hash}`;
		}

		return path;
	}

	public dispose(): void {
		window.removeEventListener("beforeunload", this.handleBeforeUnload);
	}
}
